import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { motion } from "framer-motion";

export const PrivacyPolicyPage: React.FC = () => {
  const location = useLocation();
  return (
    <section className="py-24 bg-background">
      <Helmet>
        <title>Политика конфиденциальности — SAFONOVA</title>
        <meta name="description" content="Политика конфиденциальности SAFONOVA — как мы собираем, используем и защищаем персональные данные пользователей." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/privacy'}`} />
        <meta property="og:title" content="Политика конфиденциальности — SAFONOVA" />
        <meta property="og:description" content="Политика конфиденциальности SAFONOVA — как мы собираем, используем и защищаем персональные данные пользователей." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/privacy'}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="max-w-3xl mx-auto"
        >
          <header className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-light mb-3">Политика <span className="font-medium">конфиденциальности</span></h1>
            <p className="text-foreground/70">Последнее обновление: 06.11.2025</p>
          </header>

          <article className="prose prose-lg max-w-none text-foreground/80 space-y-8">

            <section id="personal-info">
              <h2 className="text-xl font-medium mb-3">1. Общие положения</h2>
              <div className="space-y-3">
                <p>1.1. Настоящая Политика конфиденциальности регулирует порядок обработки и защиты персональных данных пользователей сайта <a href="https://safonova-brand.ru">https://safonova-brand.ru</a> (далее — «Сайт»), принадлежащего Индивидуальному предпринимателю Сафонова Анна Сергеевна, ОГРНИП 324774600132375, ИНН 773618853593 (далее — «Оператор», «мы»).</p>
                <p>1.2. Оператор обеспечивает конфиденциальность и безопасность обработки персональных данных в соответствии с Федеральным законом № 152-ФЗ «О персональных данных», Федеральным законом № 149-ФЗ «Об информации», а также другими нормативными актами РФ.</p>
                <p>1.3. Использование Сайта Пользователем означает согласие с настоящей Политикой и условиями обработки персональных данных.</p>
              </div>
            </section>

            <section id="purposes">
              <h2 className="text-xl font-medium mb-3">2. Цели обработки персональной информации</h2>
              <div className="space-y-3">
                <p>2.1. Оператор обрабатывает персональные данные в объёме, необходимом для предоставления Сервисов и исполнения договоров с Пользователем.</p>
                <p>2.2. Оператор может обрабатывать следующие категории персональных данных:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Фамилия и имя;</li>
                  <li>Дата рождения;</li>
                  <li>Номер телефона;</li>
                  <li>Адрес электронной почты;</li>
                  <li>Адрес доставки;</li>
                  <li>История заказов;</li>
                  <li>Данные об используемом устройстве, файлах cookies и IP-адресе (неперсонифицированные);</li>
                </ul>
                <p>2.3. Основные цели обработки:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>регистрации на Сайте и создания личного кабинета;</li>
                  <li>приёма и исполнения заказов;</li>
                  <li>организации доставки;</li>
                  <li>обмена информацией о статусе заказа;</li>
                  <li>предоставления сервисной и технической поддержки;</li>
                  <li>ведения бухгалтерского учёта, формирования фискальных чеков;</li>
                  <li>анализа работы Сайта и улучшения пользовательского опыта;</li>
                  <li>соблюдения требований законодательства РФ;</li>
                </ul>
                <p>2.4. Маркетинговые рассылки осуществляются только при отдельном согласии Пользователя.</p>
              </div>
            </section>

            <section id="sharing">
              <h2 className="text-xl font-medium mb-3">3. Условия передачи третьим лицам</h2>
              <div className="space-y-3">
                <p>3.1. Оператор сохраняет конфиденциальность персональных данных Пользователя, за исключением случаев, когда пользователь явно их публикует сам.</p>
                <p>3.2. Оператор может передавать персональные данные третьим лицам в следующих случаях:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>с согласия Пользователя;</li>
                  <li>для исполнения договора или оказания конкретного сервиса;</li>
                  <li>в случаях, предусмотренных законодательством;</li>
                  <li>для защиты прав и законных интересов Оператора или третьих лиц.</li>
                </ul>
                <p>3.3. При обработке персональных данных Оператор руководствуется Федеральным законом № 152-ФЗ «О персональных данных».</p>
                <p>3.4. Оператор не передает персональные данные третьим лицам, за исключением случаев, предусмотренных настоящей Политикой.</p>
              </div>
            </section>

            <section id="changes">
              <h2 className="text-xl font-medium mb-3">4. Изменение и удаление персональной информации</h2>
              <div className="space-y-3">
                <p>4.1. Данные хранятся до достижения целей обработки либо до отзыва согласия Пользователем, если иное не требуется законодательством.</p>
                <p>4.2. Пользователь может в любой момент обновить или дополнить свою информацию через личный кабинет или обратившись в службу поддержки.</p>
                <p>4.3. Оператор удаляет персональные данные по запросу Пользователя, если они больше не нужны для целей, для которых были собраны.</p>
                <p>4.4. Оператор уведомляет Пользователя о факте удаления его персональных данных.</p>
              </div>
            </section>

            <section id="cookies">
              <h2 className="text-xl font-medium mb-3">5. Cookies и счетчики</h2>
              <div className="space-y-3">
                <p>5.1. Cookies используются для персонализации сервисов, статистики и обеспечения корректной работы Сервисов.</p>
                <p>5.2. Пользователь может отключить cookies в настройках браузера, однако некоторые сервисы могут работать некорректно.</p>
              </div>
            </section>

            <section id="security">
              <h2 className="text-xl font-medium mb-3">6. Меры защиты персональной информации</h2>
              <div className="space-y-3">
                <p>6.1. Оператор принимает организационные и технические меры для защиты персональных данных от несанкционированного доступа, утраты, изменения и других неправомерных действий.</p>
                <p>6.2. Доступ к персональным данным имеют только уполномоченные сотрудники Оператора и привлечённые лица, обязанные соблюдать конфиденциальность.</p>
              </div>
            </section>

            <section id="law">
              <h2 className="text-xl font-medium mb-3">7. Изменение Политики и применимое законодательство</h2>
              <div className="space-y-3">
                <p>7.1. Оператор вправе вносить изменения в настоящую Политику. Актуальная дата обновления указывается вверху документа.</p>
                <p>7.2. Новая редакция вступает в силу с момента публикации на Сайте.</p>
              </div>
            </section>

            <section id="contact">
              <h2 className="text-xl font-medium mb-3">8. Обратная связь</h2>
              <div className="space-y-3">
                <p>8.1. По вопросам политики конфиденциальности вы можете обращаться в службу поддержки: <a href="mailto:info@safonova-brand.ru">info@safonova-brand.ru</a>.</p>
              </div>
            </section>
          </article>
        </motion.div>
      </div>
    </section>
  );
};